import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import colors from 'theme/colors';
import { useEvent } from 'context/events/Event';
import ticketsApi from 'api/requests/tickets';
import Loader from 'components/_UI/Loader';
import eventTypes from 'theme/eventTypes';
import DefaultForm from './DefaultForm';
import breakpoints from 'theme/breakpoints';
import { v4 as uuidv4 } from 'uuid';
import { SaleTypes } from 'modules/_common/SaleTypes';
import { AlertCircle, ArrowLeft, Check, Plus, Tag } from 'lucide-react';
import useToast from 'hooks/useToast';
import ToastContainer from 'components/_UI/ToastsContainer';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import ConfirmAbsorbedModal from './ConfirmAbsorbedModal';
import { committedQuantity } from './batchStock';

export default function TicketForm({
    availableProducts,
    isDraft,
    onSuccess = () => false,
    goBackFormStep = () => false
}) {
    const { event } = useEvent();
    const { toasts, toast } = useToast();

    const [loading, setLoading] = useState(false);

    const [ticketActive, setTicketActive] = useState(0);
    const [createdTickets, setCreatedTickets] = useState([]);
    const [deletedTickets, setDeletedTickets] = useState([]);
    const [deletedBatches, setDeletedBatches] = useState([]);
    const [hasErrors, setHasErrors] = useState(false);
    const [submitErrorMsg, setSubmitErrorMsg] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    // Pending absorbed value awaiting confirmation (null = no dialog). Held separately
    // because the value itself can be `false`, which is a valid pending state.
    const [absorbedConfirm, setAbsorbedConfirm] = useState(null);
    // Bumped on cancel to remount the (optimistic) Switch back to its real value.
    const [absorbedResetKey, setAbsorbedResetKey] = useState(0);

    // Products attached to the event share the tickets' absorbed value (all-or-nothing per event).
    const eventProducts = (event?.products || []).filter(product => product?.type === 'product');

    useEffect(() => {
        setHasErrors(!createdTickets.every(ticket => {
            const isTicketValid = validateTicket(ticket)

            if (!isTicketValid) {
                return false
            }

            return ticket.batches.every(batch => {
                return validateBatch(batch, ticket)
            })
        }))
    }, [createdTickets])

    useEffect(() => {
        if (!event.products || !event.products.length) {
            setCreatedTickets([
                {
                    internal_id: uuidv4(),
                    name: '',
                    batchName: 'Lote 1',
                    quantity: '',
                    description: '',
                    price: '',
                    floatPrice: 0,
                    is_active: true,
                    absorbed_service_fee: false,
                    batches: []
                }
            ])
        } else {
            const ticketsToEdit = event.products
                .filter(el => el?.type === 'ticket')
                .map(ticket => {
                    if (!ticket.batches || !ticket.batches.length) {
                        return ticket
                    }

                    const adaptedTicket = {
                        ...ticket,
                        floatPrice: Number.parseFloat(ticket.price),
                        is_active: Boolean(ticket.batches[0].is_active),
                        batchName: ticket.batches[0].name,
                        batch_id: ticket.batches[0].id,
                        hidden_code: ticket.hidden_code ?? null,
                        // Piso da quantidade, fixado aqui porque depende do lote
                        // como o servidor mandou — `quantity` muda ao digitar.
                        committed: committedQuantity(ticket.batches[0]),
                        batches: ticket.batches.slice(1).map(batch => {
                            return {
                                ...batch,
                                floatPrice: Number.parseFloat(batch.price),
                                committed: committedQuantity(batch),
                                internal_id: uuidv4()
                            }
                        })
                    }

                    if (adaptedTicket.sale_type && adaptedTicket.sale_type === SaleTypes.PERIOD) {
                        adaptedTicket.sale_start = ticket.batches[0].sale_start
                        adaptedTicket.sale_end = ticket.batches[0].sale_end
                    }

                    return adaptedTicket
                });

            setCreatedTickets(ticketsToEdit);
        }
    }, [event]);

    function isEdit() {
        return event?.products?.some(product => product.type === 'ticket');
    }

    function validateTicket(ticket) {
        const isNameValid = (ticket.name && ticket.name.length <= 30)
        const isDescriptionValid = (ticket.description ? ticket.description.length <= 80 : true);
        const isHiddenCodeValid = (ticket.hidden_code ? ticket.hidden_code.length <= 30 : true);
        const isBatchNameValid = (ticket.batchName && ticket.batchName.length > 0);
        const isQuantityValid = (ticket.quantity && Number(ticket.quantity) > 0);
        const isSalePeriodValid = ticket.sale_type === SaleTypes.PERIOD ? ticket.sale_start && ticket.sale_end : true;
        let isPriceValid = (ticket.floatPrice !== undefined && ticket.floatPrice !== null);

        return (
            isNameValid &&
            isDescriptionValid &&
            isHiddenCodeValid &&
            isPriceValid &&
            isBatchNameValid &&
            isQuantityValid &&
            isSalePeriodValid
        )
    }

    function validateBatch(batch, ticket) {
        let isPriceValid = batch.floatPrice !== undefined && batch.floatPrice !== null
        const isQuantityValid = batch.quantity && Number(batch.quantity) > 0
        const isSalePeriodValid = ticket.sale_type === SaleTypes.PERIOD ? batch.sale_start && batch.sale_end : true

        return (
            isPriceValid &&
            isQuantityValid &&
            isSalePeriodValid
        )
    }

    function ticketHasError(ticket) {
        const isTicketValid = validateTicket(ticket)
        if (!isTicketValid) return true
        return !ticket.batches.every(batch => validateBatch(batch, ticket))
    }

    function duplicateTicket(index) {
        const source = createdTickets[index]
        const baseName = source.name ? source.name.replace(/ \(cópia\)$/, '') : ''
        const cloned = {
            ...JSON.parse(JSON.stringify(source)),
            internal_id: uuidv4(),
            id: undefined,
            batch_id: undefined,
            name: baseName ? `${baseName} (cópia)` : '',
            batches: source.batches.map(b => ({
                ...b,
                internal_id: uuidv4(),
                id: undefined,
            }))
        }
        const newTickets = [...createdTickets, cloned]
        setCreatedTickets(newTickets)
        setTicketActive(newTickets.length - 1)
    }

    function onDisabledSubmitClick() {
        const errorTickets = createdTickets
            .map((t, i) => ticketHasError(t) ? i + 1 : null)
            .filter(Boolean)

        if (errorTickets.length) {
            setSubmitErrorMsg(`Preencha os campos obrigatórios: Ingresso ${errorTickets.join(', ')}`)
            setTimeout(() => setSubmitErrorMsg(''), 4000)
        }
    }

    function addNewForm() {
        setCreatedTickets([...createdTickets, {
            internal_id: uuidv4(),
            name: '',
            batchName: 'Lote 1',
            quantity: '',
            description: '',
            price: '',
            floatPrice: 0,
            is_active: true,
            absorbed_service_fee: false,
            batches: []
        }]);
        setTicketActive(createdTickets.length)
    };

    function applyAbsorbedToAll(value) {
        setCreatedTickets(tickets => tickets.map(ticket => ({
            ...ticket,
            absorbed_service_fee: value
        })));
    }

    // Toggling the switch on any ticket changes it for every ticket and product in the
    // event. Warn first whenever more than the current ticket would be affected.
    function handleRequestAbsorbedChange(value) {
        const affectedCount = (createdTickets.length - 1) + eventProducts.length;

        if (affectedCount > 0) {
            setAbsorbedConfirm(value);
            return;
        }

        applyAbsorbedToAll(value);
    }

    function confirmAbsorbedChange() {
        applyAbsorbedToAll(absorbedConfirm);
        setAbsorbedConfirm(null);
    }

    function cancelAbsorbedChange() {
        // The Switch already flipped optimistically; remount it so it reflects the unchanged value.
        setAbsorbedConfirm(null);
        setAbsorbedResetKey(key => key + 1);
    }

    function onSubmitForm(from) {
        if (from === 'createNewForm') {
            addNewForm()
        }

        if (from === 'submitForm') {
            const hasInactiveBatchWithData = createdTickets.some(ticket =>
                ticket.batches.some(batch =>
                    !batch.is_active && (batch.floatPrice > 0 || batch.quantity)
                )
            )
            if (hasInactiveBatchWithData) {
                toast.warning('Alguns lotes com dados preenchidos estão inativos.')
            }

            if (isEdit()) {
                editTicket()
            } else {
                createTicket()
            }
        }
    }

    function moveTicket(from, to) {
        if (to < 0 || to >= createdTickets.length) return
        const newTickets = [...createdTickets]
        const [moved] = newTickets.splice(from, 1)
        newTickets.splice(to, 0, moved)
        setCreatedTickets(newTickets)

        if (ticketActive === from) {
            setTicketActive(to)
        } else if (ticketActive === to) {
            setTicketActive(from)
        }
    }

    async function createTicket() {
        try {
            setLoading(true);

            const payload = {
                type: 'tickets',
                event_id: event?.id,
                products: createdTickets.map(ticket => {
                    const firstBatch = {
                        price: ticket.floatPrice,
                        name: ticket.batchName,
                        quantity: ticket.quantity,
                        is_active: ticket.is_active,
                    }

                    if (ticket.sale_start && ticket.sale_end) {
                        firstBatch.sale_start = ticket.sale_start
                        firstBatch.sale_end = ticket.sale_end
                    }

                    const ticketPayload = {
                        event_id: event?.id,
                        is_active: ticket.is_active,
                        type: 'ticket',
                        name: ticket.name,
                        price: ticket.floatPrice,
                        quantity: Number(ticket.quantity),
                        sale_type: ticket.sale_type,
                        hidden_code: ticket.hidden_code || null,
                        absorbed_service_fee: ticket.absorbed_service_fee ?? false,
                        batches: [firstBatch, ...ticket.batches.map(batch => {
                            const adaptedBatch = {
                                price: batch.floatPrice,
                                name: batch.name,
                                quantity: batch.quantity,
                                is_active: batch.is_active,
                            }

                            if (batch?.sale_start && batch?.sale_end) {
                                adaptedBatch.sale_start = batch.sale_start
                                adaptedBatch.sale_end = batch.sale_end
                            }

                            return adaptedBatch
                        })],
                    }

                    if (ticket.description) {
                        firstBatch.description = ticket.description
                        ticketPayload.description = ticket.description
                    }

                    if (event.classification.id === Number(eventTypes.free.id)) {
                        ticketPayload.is_free = true
                        ticketPayload.price = 0
                    }

                    return ticketPayload
                })
            }

            const { data } = await ticketsApi.createBulk(payload);
            onSuccess({
                ...data,
                products: availableProducts
            })

            if (!availableProducts.length) {
                if (!isDraft) {
                    removeDraft();
                }
            }
        } catch (error) {
            toast.error('Erro ao criar ingressos. Tente novamente.')
            setSubmitErrorMsg('Falha ao salvar. Verifique sua conexão e tente novamente.')
            setTimeout(() => setSubmitErrorMsg(''), 5000)
        } finally {
            setLoading(false);
        }
    }

    async function editTicket() {
        try {
            setLoading(true);
            const payload = {
                type: 'tickets',
                event_id: event?.id,
                products: createdTickets.map(ticket => {
                    const firstBatch = {
                        id: ticket.batch_id,
                        price: ticket.floatPrice,
                        name: ticket.batchName,
                        quantity: ticket.quantity,
                        is_active: ticket.is_active,
                    }

                    if (ticket.sale_start && ticket.sale_end) {
                        firstBatch.sale_start = ticket.sale_start
                        firstBatch.sale_end = ticket.sale_end
                    }

                    const ticketPayload = {
                        event_id: event?.id,
                        id: ticket.id,
                        is_active: ticket.is_active,
                        type: 'ticket',
                        name: ticket.name,
                        price: ticket.floatPrice,
                        quantity: Number(ticket.quantity),
                        sale_type: ticket.sale_type,
                        hidden_code: ticket.hidden_code || null,
                        absorbed_service_fee: ticket.absorbed_service_fee ?? false,
                        batches: [firstBatch, ...ticket.batches.map(batch => {
                            const adaptedBatch = {
                                id: batch.id,
                                price: batch.floatPrice,
                                name: batch.name,
                                quantity: batch.quantity,
                                is_active: batch.is_active,
                            }

                            if (batch?.sale_start && batch?.sale_end) {
                                adaptedBatch.sale_start = batch.sale_start
                                adaptedBatch.sale_end = batch.sale_end
                            }

                            return adaptedBatch
                        })],
                    }

                    if (ticket.description) {
                        firstBatch.description = ticket.description
                        ticketPayload.description = ticket.description
                    }

                    if (event.classification.id === Number(eventTypes.free.id)) {
                        ticketPayload.is_free = true
                        ticketPayload.price = 0
                    }

                    return ticketPayload
                })
            }

            if (deletedTickets.length || deletedBatches.length) {
                const deletePayload = {}

                if (deletedTickets.length) {
                    deletePayload.productsToDelete = deletedTickets
                }

                if (deletedBatches.length) {
                    deletePayload.batchesToDelete = deletedBatches
                }

                await ticketsApi.deleteBulk({
                    data: deletePayload
                })
            }

            // The backend normalizes the absorbed value across all of the event's products,
            // so the tickets bulk call alone keeps the whole event consistent.
            await ticketsApi.updateBulk(payload)

            onSuccess()

            if (!availableProducts.length) {
                if (!isDraft) {
                    removeDraft();
                }
            }
        } catch (error) {
            toast.error('Erro ao atualizar ingressos. Tente novamente.')
            setSubmitErrorMsg('Falha ao salvar. Verifique sua conexão e tente novamente.')
            setTimeout(() => setSubmitErrorMsg(''), 5000)
        } finally {
            setLoading(false)
        }
    }

    async function removeDraft() {
        try {
            await ticketsApi.removeDraft(event?.slug);
        } catch (error) {
        } finally {
            setLoading(false);
        }
    }

    function confirmDeleteTicket(index) {
        const newTickets = [...createdTickets]
        const removed = newTickets.splice(index, 1)

        if (removed[0]?.id) {
            setDeletedTickets([
                ...deletedTickets,
                removed[0].id
            ])
            setDeletedBatches((removedBatches) => {
                return [
                    ...removedBatches,
                    ...removed[0].batches.filter(batch => batch.id ?? false).map(batch => batch.id),
                    removed[0].batch_id,
                ]
            })
        }

        setCreatedTickets(newTickets)

        if (ticketActive === index) {
            setTicketActive(false);
        }

        if (ticketActive > index) {
            setTicketActive(ticketActive - 1);
        }

        setDeleteConfirm(null)
    }


    return (
        <FormContainer>
            <ToastContainer toasts={toasts} />

            <PageHeader>
                <HeaderIconCircle>
                    <Tag size={20} color={colors.occasionalPurple} />
                </HeaderIconCircle>
                <div>
                    <PageTitle>{isEdit() ? 'Atualizar' : 'Criar'} ingressos</PageTitle>
                    <PageSubtitle>
                        {createdTickets.length} {createdTickets.length === 1 ? 'ingresso' : 'ingressos'} configurado{createdTickets.length !== 1 ? 's' : ''}
                    </PageSubtitle>
                </div>
            </PageHeader>

            {createdTickets?.map((ticket, index) => {
                return (
                    <DefaultForm
                        key={ticket.internal_id}
                        ticketIndex={index}
                        onDeleteTicket={() => setDeleteConfirm(index)}
                        onDuplicateTicket={() => duplicateTicket(index)}
                        ticket={ticket}
                        setTicket={setCreatedTickets}
                        isUnique={createdTickets.length === 0}
                        selected={ticketActive == (index)}
                        onTicketClick={() => setTicketActive(ticketActive === index ? null : index)}
                        setDeletedBatches={setDeletedBatches}
                        onRequestAbsorbedChange={handleRequestAbsorbedChange}
                        absorbedResetKey={absorbedResetKey}
                        hasTicketError={ticketHasError(ticket)}
                        onMoveUp={() => moveTicket(index, index - 1)}
                        onMoveDown={() => moveTicket(index, index + 1)}
                        canMoveUp={index > 0}
                        canMoveDown={index < createdTickets.length - 1}
                    />
                )
            })}

            <ConfirmDeleteModal
                isOpen={deleteConfirm !== null}
                title="Excluir ingresso"
                message="Tem certeza que deseja excluir este ingresso? Esta ação não pode ser desfeita."
                onConfirm={() => confirmDeleteTicket(deleteConfirm)}
                onCancel={() => setDeleteConfirm(null)}
            />

            <ConfirmAbsorbedModal
                isOpen={absorbedConfirm !== null}
                message={eventProducts.length
                    ? 'Isso altera a taxa de serviço de todos os ingressos e produtos deste evento.'
                    : 'Isso altera a taxa de serviço de todos os ingressos deste evento.'}
                onConfirm={confirmAbsorbedChange}
                onCancel={cancelAbsorbedChange}
            />

            <AddTicketCard
                disabled={hasErrors}
                onClick={() => !hasErrors && onSubmitForm('createNewForm')}
            >
                <AddTicketIcon disabled={hasErrors}>
                    <Plus size={20} />
                </AddTicketIcon>
                <AddTicketText>
                    <AddTicketTitle disabled={hasErrors}>Novo ingresso</AddTicketTitle>
                    <AddTicketHint>Clique para adicionar outro tipo de ingresso</AddTicketHint>
                </AddTicketText>
            </AddTicketCard>

            <BottomSpacer />

            <BottomBar>
                {loading ? (
                    <LoaderWrapper>
                        <Loader />
                    </LoaderWrapper>
                ) : (
                    <>
                        {submitErrorMsg && (
                            <ErrorBanner>
                                <AlertCircle size={14} />
                                {submitErrorMsg}
                            </ErrorBanner>
                        )}
                        <BottomBarContent>
                            <BackButton onClick={() => goBackFormStep(1)}>
                                <ArrowLeft size={16} />
                                Voltar
                            </BackButton>
                            <SubmitButton
                                disabled={hasErrors}
                                onClick={() => {
                                    if (hasErrors) {
                                        onDisabledSubmitClick()
                                        return
                                    }
                                    onSubmitForm('submitForm')
                                }}
                            >
                                <Check size={16} />
                                {isEdit() ? 'Salvar alterações' : 'Criar ingressos'}
                            </SubmitButton>
                        </BottomBarContent>
                    </>
                )}
            </BottomBar>
        </FormContainer >
    );
}


const FormContainer = styled.div`
    overflow-x: auto;
`;

const PageHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid ${colors.night}11;
`;

const HeaderIconCircle = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: ${colors.occasionalPurple}10;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const PageTitle = styled.h2`
    font-size: 20px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0;
    line-height: 1.3;
`;

const PageSubtitle = styled.p`
    font-size: 13px;
    color: ${colors.night};
    margin: 2px 0 0 0;
`;

const AddTicketCard = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 20px;
    border: 2px dashed ${({ disabled }) => disabled ? colors.night + '18' : colors.occasionalPurple + '33'};
    border-radius: 12px;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.2s ease;
    margin-top: 8px;

    ${({ disabled }) => !disabled && `
        &:hover {
            border-color: ${colors.occasionalPurple}66;
            background: ${colors.occasionalPurple}05;
        }
    `};
`;

const AddTicketIcon = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${({ disabled }) => disabled ? colors.night + '10' : colors.occasionalPurple + '12'};
    color: ${({ disabled }) => disabled ? colors.night + '44' : colors.occasionalPurple};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s ease;
`;

const AddTicketText = styled.div``;

const AddTicketTitle = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: ${({ disabled }) => disabled ? colors.night + '55' : colors.occasionalPurple};
    transition: color 0.2s ease;
`;

const AddTicketHint = styled.div`
    font-size: 12px;
    color: ${colors.night}88;
    margin-top: 2px;
`;

const BottomSpacer = styled.div`
    height: 120px;
`;

const BottomBar = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background: #fff;
    border-top: 1px solid ${colors.night}15;
    box-shadow: 0 -4px 20px ${colors.night}0A;
    z-index: 10;
    padding: 14px 24px;

    @media screen and (min-width: ${breakpoints.md}) {
        width: 75%;
    }
`;

const BottomBarContent = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    max-width: 500px;
    margin: 0 auto;
`;

const LoaderWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 8px 0;
`;

const ErrorBanner = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 500;
    color: ${colors.danger};
    background: ${colors.danger}0A;
    border-radius: 8px;
    padding: 8px 14px;
    margin-bottom: 10px;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
`;

const BackButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex: 1;
    padding: 12px 20px;
    border: 1.5px solid ${colors.night}22;
    border-radius: 10px;
    background: transparent;
    color: ${colors.night};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.night}08;
        border-color: ${colors.night}44;
    }
`;

const SubmitButton = styled.button.withConfig({
    shouldForwardProp: (prop) => prop !== 'disabled',
})`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex: 1;
    padding: 12px 20px;
    border: none;
    border-radius: 10px;
    background: ${({ disabled }) => disabled ? colors.night + '18' : colors.occasionalPurple};
    color: ${({ disabled }) => disabled ? colors.night + '55' : '#fff'};
    font-size: 14px;
    font-weight: 700;
    cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
    transition: all 0.15s ease;

    ${({ disabled }) => !disabled && `
        &:hover {
            background: ${colors.secondaryPurple};
        }
    `};
`;
